import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { faBars } from '@fortawesome/pro-duotone-svg-icons'

import { useAuth } from '../../_hooks/use-auth'
import { useCelebration } from '../../_hooks/use-celebration'
import { useQuickPassNotifications } from '../../_hooks/use-quickpass-notifications'
import { useMessagesUnread } from '../../_hooks/use-messages-unread'
import { useResidentMessagesUnread } from '../../_hooks/use-resident-messages-unread'
import { useExperience } from '../../_hooks/use-experience'
import { NavBadgeCounts } from '../../_types/navigation'
import Icon from '../icon/Icon'
import NotificationsBell from '../notifications-bell/NotificationsBell'
import NotificationsDrawer from '../notifications-drawer/NotificationsDrawer'
import MobileNavDrawer from '../mobile-nav-drawer/MobileNavDrawer'
import PingsBell from '../pings-bell/PingsBell'
import RenterAvatar from '../renter-avatar/RenterAvatar'
import {
  HeaderRoot,
  HeaderLeft,
  HeaderRight,
  Wordmark,
  NavList,
  NavLink,
  NavLinkBadge,
  ModeSwitchButton,
  DesktopAvatarSlot,
  MenuButton,
  SignOutButton,
} from './styled'
import { AppHeaderProps } from './types'
import {
  getSearchNavEntries,
  RESIDENT_NAV_ENTRIES,
  resolveNavBadgeCount,
} from './helpers'
import { formatBadgeCount } from '../../_utilities/format-badge-count'

/**
 * Top-of-page header for signed-in surfaces. Brand wordmark on the left,
 * primary navigation in the middle (hidden on auth-only pages), bell +
 * sign-out on the right. Owns the notifications drawer open state so the
 * bell + drawer stay in sync without crossing the provider tree. Below the
 * `lg` breakpoint the inline nav collapses into a hamburger that opens the
 * MobileNavDrawer.
 *
 * @param {AppHeaderProps} props
 * @returns {JSX.Element}
 */
const AppHeader = ({ showNavigation = true }: AppHeaderProps): JSX.Element => {
  const location = useLocation()
  const navigate = useNavigate()
  const { signOut } = useAuth()
  const { isDrawerOpen, notifications, openDrawer, closeDrawer } =
    useQuickPassNotifications()
  const { totalUnread } = useMessagesUnread()
  const { unreadCount: residentMessageUnreadCount } = useResidentMessagesUnread()
  const { mode, canSwitch, setMode, isResolved } = useExperience()
  const { celebrate } = useCelebration()

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false)

  const navEntries =
    mode === 'resident' ? RESIDENT_NAV_ENTRIES : getSearchNavEntries()

  let homePath = '/'
  if (isResolved === true) {
    homePath = mode === 'resident' ? '/my-home' : '/dashboard'
  }

  const preferenceMatchCount = useMemo<number>(
    () =>
      notifications.filter(
        notification =>
          notification.type === 'preference-match' &&
          notification.isRead === false,
      ).length,
    [notifications],
  )

  const badgeCounts: NavBadgeCounts = {
    preferenceMatchCount,
    unreadMessageCount: totalUnread,
    residentMessageUnreadCount,
  }

  useEffect(() => {
    const desktopQuery = window.matchMedia('(min-width: 1024px)')

    const handleViewportChange = (event: MediaQueryListEvent): void => {
      if (event.matches === true) {
        setIsMenuOpen(false)
      }
    }

    desktopQuery.addEventListener('change', handleViewportChange)
    return () => {
      desktopQuery.removeEventListener('change', handleViewportChange)
    }
  }, [])

  const wordmarkClickCountReference = useRef<number>(0)
  const wordmarkResetHandleReference = useRef<ReturnType<
    typeof setTimeout
  > | null>(null)

  const handleWordmarkClick = (): void => {
    wordmarkClickCountReference.current += 1

    if (wordmarkResetHandleReference.current !== null) {
      clearTimeout(wordmarkResetHandleReference.current)
    }

    wordmarkResetHandleReference.current = setTimeout(() => {
      wordmarkClickCountReference.current = 0
    }, 1500)

    if (wordmarkClickCountReference.current >= 7) {
      celebrate()
      wordmarkClickCountReference.current = 0
      // eslint-disable-next-line no-console -- intentional easter-egg flavour, gated behind a 7-click secret
      console.info('🎉 You found the secret. Welcome to the renter cult.')
    }
  }

  const handleSwitchMode = (): void => {
    const nextMode = mode === 'resident' ? 'search' : 'resident'
    setMode(nextMode)
    navigate(nextMode === 'resident' ? '/my-home' : '/dashboard')
  }

  const handleSignOut = async (): Promise<void> => {
    try {
      await signOut()
      navigate('/sign-in')
    } catch (error) {
      console.error('Sign-out failed', error)
    }
  }

  const showNavigationControls = showNavigation === true && isResolved === true

  return (
    <>
      <HeaderRoot>
        <HeaderLeft>
          <Wordmark to={homePath} onClick={handleWordmarkClick}>
            QuickPass
          </Wordmark>
          {showNavigationControls === true ? (
            <NavList>
              {navEntries.map(entry => {
                const badgeCount = resolveNavBadgeCount(entry, badgeCounts)
                return (
                  <NavLink
                    key={entry.to}
                    to={entry.to}
                    isActive={location.pathname === entry.to}
                  >
                    {entry.label}
                    {badgeCount > 0 ? (
                      <NavLinkBadge>
                        {formatBadgeCount(badgeCount)}
                      </NavLinkBadge>
                    ) : null}
                  </NavLink>
                )
              })}
            </NavList>
          ) : null}
        </HeaderLeft>
        <HeaderRight>
          {isResolved === true && canSwitch === true ? (
            <ModeSwitchButton type="button" onClick={handleSwitchMode}>
              Switch to {mode === 'resident' ? 'Search' : 'Resident'} →
            </ModeSwitchButton>
          ) : null}
          <PingsBell onClick={() => navigate('/pings')} />
          <NotificationsBell onClick={openDrawer} />
          <DesktopAvatarSlot>
            <RenterAvatar size="md" />
          </DesktopAvatarSlot>
          <SignOutButton type="button" onClick={handleSignOut}>
            Sign out
          </SignOutButton>
          {showNavigationControls === true ? (
            <MenuButton
              type="button"
              onClick={() => setIsMenuOpen(true)}
              aria-label="Open menu"
            >
              <Icon icon={faBars} />
            </MenuButton>
          ) : null}
        </HeaderRight>
      </HeaderRoot>

      <NotificationsDrawer isOpen={isDrawerOpen} onClose={closeDrawer} />

      <MobileNavDrawer
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        navEntries={navEntries}
        activePath={location.pathname}
        badgeCounts={badgeCounts}
        mode={mode}
        canSwitch={canSwitch}
        onSwitchMode={handleSwitchMode}
        onSignOut={handleSignOut}
      />
    </>
  )
}

export default AppHeader
